import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAnonUsageTimer } from '../hooks/useAnonUsageTimer';

/**
 * Auth gate overlay for anonymous users:
 *   - 5 min: a dismissible amber banner at the bottom "Create a free account"
 *   - 10 min: a full-screen modal — must sign up (or log in) to keep using the app
 *
 * Safe routes (`/login`, `/register`, `/pricing`, legal pages) are NEVER blocked,
 * so the user always has a place to convert.
 */
const SAFE_PATHS = ['/login', '/register', '/pricing', '/privacy', '/terms', '/refund', '/contact', '/about'];

export default function AnonAuthGate() {
  const { signedIn } = useAuth();
  const { showPrompt, showWarning, showBlock, minutes, secondsUntilBlock } = useAnonUsageTimer(signedIn);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  if (signedIn) return null;
  const onSafePath = SAFE_PATHS.some((p) => location.pathname.startsWith(p));

  // ─── 10-minute hard block ───
  if (showBlock && !onSafePath) {
    return (
      <div
        className="fixed inset-0 z-[9999] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-6"
        data-testid="anon-auth-block"
      >
        <div className="max-w-md w-full bg-gradient-to-br from-slate-900 to-slate-800 border border-amber-500/40 rounded-2xl shadow-[0_0_60px_rgba(255,180,80,0.2)] p-8 text-center">
          <div className="text-5xl mb-3">💡</div>
          <h2 className="text-2xl font-bold text-white mb-2">
            You're hooked — now save your work
          </h2>
          <p className="text-slate-300 text-sm mb-6">
            You've explored Facade Lighting Studio for <span className="font-semibold text-amber-400">{minutes} minutes</span>.
            Create a free account to keep designing, unlock AI Finalize, and save your projects.
          </p>
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => navigate('/register')}
              data-testid="anon-auth-block-register"
              className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-3 rounded-lg shadow-lg"
            >
              Create free account — 3 exports / month
            </button>
            <button
              type="button"
              onClick={() => navigate('/login')}
              data-testid="anon-auth-block-login"
              className="w-full bg-slate-800 hover:bg-slate-700 text-white py-2.5 rounded-lg border border-slate-700 text-sm"
            >
              I already have an account — Log in
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mt-5">
            Free forever. No credit card. 3 exports every month.
          </p>
        </div>
      </div>
    );
  }

  // ─── 9:30 warning — friendly "30 seconds left" heads-up ───
  if (showWarning && !onSafePath) {
    return (
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9998] max-w-md w-[calc(100%-2rem)] bg-gradient-to-r from-rose-600/95 to-orange-600/95 backdrop-blur-md border border-rose-300/50 text-white rounded-xl shadow-2xl px-5 py-3 flex items-center gap-4"
        data-testid="anon-auth-warning"
      >
        <div className="flex-1">
          <div className="text-sm font-semibold">⏳ {secondsUntilBlock} seconds left</div>
          <p className="text-[12px] text-white/90">
            Sign up free to keep going — takes 10 seconds.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/register')}
          data-testid="anon-auth-warning-register"
          className="flex-shrink-0 bg-white text-rose-700 hover:bg-rose-50 text-xs font-semibold px-3 py-2 rounded-lg"
        >
          Sign up free
        </button>
      </div>
    );
  }

  // ─── 5-minute soft prompt (dismissible) ───
  if (showPrompt && !bannerDismissed && !onSafePath) {
    return (
      <div
        className="fixed bottom-4 right-4 z-[9998] max-w-sm bg-gradient-to-br from-amber-500/95 to-orange-600/95 backdrop-blur-md border border-amber-300/50 text-white rounded-xl shadow-2xl p-4"
        data-testid="anon-auth-prompt"
      >
        <button
          type="button"
          onClick={() => setBannerDismissed(true)}
          data-testid="anon-auth-prompt-dismiss"
          className="absolute top-2 right-2 text-white/70 hover:text-white text-lg leading-none"
          aria-label="Dismiss"
        >
          ×
        </button>
        <div className="text-sm font-semibold mb-1">Enjoying the studio?</div>
        <p className="text-[13px] text-white/90 mb-3">
          Create a free account to keep your designs and unlock AI Finalize.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate('/register')}
            data-testid="anon-auth-prompt-register"
            className="flex-1 bg-white text-amber-700 hover:bg-amber-50 text-xs font-semibold px-3 py-1.5 rounded-lg"
          >
            Sign up free
          </button>
          <button
            type="button"
            onClick={() => navigate('/login')}
            data-testid="anon-auth-prompt-login"
            className="flex-1 bg-black/30 hover:bg-black/40 text-white text-xs font-semibold px-3 py-1.5 rounded-lg"
          >
            Log in
          </button>
        </div>
      </div>
    );
  }

  return null;
}
